import { logger } from '@fe/core';
import { Color } from '@maplibre/maplibre-gl-style-spec';
import { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import { WindBarbsResult } from '../../../worker/store/raster/winds';
import { TextSpriteData } from '../text/text-renderer';
import fragmentSource from './temperature-grid.fragment.glsl?raw';
import vertexSource from './temperature-grid.vert.glsl?raw';
import { TemperatureTextRenderer } from './temperature-text-renderer';

type ZoomExpression = number | ((zoom: number) => number);

const RENDER_ZOOM_THRESHOLD = 4;
const TEXT_PIXEL_OFFSET: [number, number] = [10, 10];

export class TemperatureGridLayer implements CustomLayerInterface {
    readonly type = 'custom';
    readonly renderingMode?: '2d';
    private shaderMap = new Map<string, WebGLProgram>();
    private gl: WebGL2RenderingContext | null = null;
    private aPos: GLint = 0;
    private aCenter: GLint = 0;
    private aTexCoords: GLint = 0;
    private aTexOffset: GLint = 0;
    private aVisibility: GLint = 0;

    private vertexBuffer: WebGLBuffer | null = null;
    private centerBuffer: WebGLBuffer | null = null;
    private texCoordsBuffer: WebGLBuffer | null = null;
    private texOffsetsBuffer: WebGLBuffer | null = null;
    private visibilityBuffer: WebGLBuffer | null = null;
    private texture: WebGLTexture | null = null;
    private map: MaplibreMap | null = null;
    private instanceCount = 0;
    private gridWidth: number | null = null;
    private gridHeight: number | null = null;
    private _size: ZoomExpression = 24;
    private _opacity = 1.0;
    private imageWidth = 0;
    private imageHeight = 0;
    private imgsCount = 0;
    private lastZoom = -1;
    private visibilityData: Uint8Array | null = null;
    private textRenderer: TemperatureTextRenderer;
    private _color: [number, number, number, number] = [0, 0, 0, 0];

    constructor(public readonly id: string) {
        this.textRenderer = new TemperatureTextRenderer();
    }

    get opacity(): number {
        return this._opacity;
    }

    set opacity(value: number) {
        this._opacity = value;
        this.map?.triggerRepaint();
    }

    get size() {
        return this._size;
    }

    set size(value: ZoomExpression) {
        this._size = value;
        this.map?.triggerRepaint();
    }

    set color(value: string | null) {
        if (!value) {
            return;
        }
        const color = Color.parse(value);
        this._color = [color!.r, color!.g, color!.b, color!.a];
        this.map?.triggerRepaint();
    }

    private getShader(gl: WebGLRenderingContext | WebGL2RenderingContext, shaderDescription: CustomRenderMethodInput['shaderData']) {
        // Pick a shader based on the current projection, defined by `variantName`.
        if (this.shaderMap.has(shaderDescription.variantName)) {
            return this.shaderMap.get(shaderDescription.variantName);
        }

        const vs = vertexSource
            .replace('__define__', shaderDescription.define)
            .replace('__prelude__', shaderDescription.vertexShaderPrelude);

        // create a vertex shader
        const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
        gl.shaderSource(vertexShader, vs);
        gl.compileShader(vertexShader);
        if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(vertexShader));
        }

        // create a fragment shader
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
        gl.shaderSource(fragmentShader, fragmentSource);
        gl.compileShader(fragmentShader);
        if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(fragmentShader));
        }

        // link the two shaders into a WebGL program
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error(gl.getProgramInfoLog(program));
        }

        this.aPos = gl.getAttribLocation(program, 'a_pos');
        this.aCenter = gl.getAttribLocation(program, 'a_center');
        this.aTexCoords = gl.getAttribLocation(program, 'a_tex_coords');
        this.aTexOffset = gl.getAttribLocation(program, 'a_tex_offset');
        this.aVisibility = gl.getAttribLocation(program, 'a_visibility');

        this.shaderMap.set(shaderDescription.variantName, program);

        return program;
    }

    setData(data: WindBarbsResult) {
        if (!this.gl) {
            return;
        }
        const { mercatorPositions: positions, temperatureBuffer, width, height } = data;
        this.gridWidth = width;
        this.gridHeight = height;

        if (positions.length !== temperatureBuffer.length * 2) {
            logger.warn('Invalid data');
            return;
        }
        this.instanceCount = temperatureBuffer.length;
        const gl = this.gl;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, 1, 1, 1, -1, -1, 1, -1]), gl.STATIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.centerBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordsBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 1, 1, 1, 0, 0, 1, 0]), gl.STATIC_DRAW);

        this.visibilityData = new Uint8Array(this.instanceCount);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.visibilityBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, this.visibilityData, gl.DYNAMIC_DRAW);

        this.lastZoom = -1;

        const spriteData: TextSpriteData | null = this.textRenderer.getSpriteData(temperatureBuffer);
        if (!spriteData) {
            logger.warn('No sprite data');
            return;
        }

        gl.bindBuffer(gl.ARRAY_BUFFER, this.texOffsetsBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, spriteData.offsets, gl.STATIC_DRAW);

        this.loadCanvasTexture(spriteData);
    }

    private loadCanvasTexture(spriteData: TextSpriteData) {
        const gl = this.gl;
        if (!gl || !this.texture) {
            return;
        }

        this.imageWidth = spriteData.width;
        this.imageHeight = spriteData.height;
        this.imgsCount = spriteData.length;

        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, spriteData.data as OffscreenCanvas);

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    }

    onAdd(map: MaplibreMap, gl: WebGL2RenderingContext): void {
        this.map = map;
        this.gl = gl;
        this.vertexBuffer = gl.createBuffer();
        this.centerBuffer = gl.createBuffer();
        this.texCoordsBuffer = gl.createBuffer();
        this.texOffsetsBuffer = gl.createBuffer();
        this.visibilityBuffer = gl.createBuffer();
        this.texture = gl.createTexture();
    }

    onRemove(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.map = null;
        gl.deleteBuffer(this.vertexBuffer);
        gl.deleteBuffer(this.centerBuffer);
        gl.deleteBuffer(this.texCoordsBuffer);
        gl.deleteBuffer(this.texOffsetsBuffer);
        gl.deleteBuffer(this.visibilityBuffer);
        gl.deleteTexture(this.texture);

        gl.disableVertexAttribArray(this.aPos);
        gl.disableVertexAttribArray(this.aCenter);
        gl.disableVertexAttribArray(this.aTexCoords);
        gl.disableVertexAttribArray(this.aTexOffset);
        gl.disableVertexAttribArray(this.aVisibility);

        this.vertexBuffer = null;
        this.centerBuffer = null;
        this.texCoordsBuffer = null;
        this.texOffsetsBuffer = null;
        this.visibilityBuffer = null;
        this.texture = null;
        this.visibilityData = null;

        for (const program of this.shaderMap.values()) {
            gl.deleteProgram(program);
        }

        this.shaderMap.clear();
        this.gl = null;
    }

    render(_gl: WebGLRenderingContext | WebGL2RenderingContext, args: CustomRenderMethodInput) {
        if (!this.map) {
            return;
        }
        const gl = _gl as WebGL2RenderingContext;
        const currentZoom = this.map.getZoom();

        if (currentZoom < RENDER_ZOOM_THRESHOLD) {
            return;
        }

        if (Math.abs(currentZoom - this.lastZoom) >= 0.5) {
            this.updateVisibilityBuffer(currentZoom);
            this.lastZoom = currentZoom;
        }

        const globe = args.shaderData.variantName === 'globe';
        (globe ? [0] : this.getWorldOffsets()).forEach((offset) => {
            const program = this.getShader(gl, args.shaderData)!;
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.SRC_ALPHA, gl.DST_ALPHA);

            gl.useProgram(program);
            gl.uniform4f(gl.getUniformLocation(program, 'u_color'), ...this._color);
            gl.uniform2f(gl.getUniformLocation(program, 'u_canvas_size'), gl.drawingBufferWidth, gl.drawingBufferHeight);
            gl.uniform2f(gl.getUniformLocation(program, 'u_text_pixel_offset'), ...TEXT_PIXEL_OFFSET);

            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_fallback_matrix'),
                false,
                args.defaultProjectionData.fallbackMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_matrix'),
                false,
                args.defaultProjectionData.mainMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniform4f(
                gl.getUniformLocation(program, 'u_projection_tile_mercator_coords'),
                ...args.defaultProjectionData.tileMercatorCoords
            );

            gl.uniform4f(gl.getUniformLocation(program, 'u_projection_clipping_plane'), ...args.defaultProjectionData.clippingPlane);
            gl.uniform1f(gl.getUniformLocation(program, 'u_projection_transition'), args.defaultProjectionData.projectionTransition);
            gl.uniform1f(gl.getUniformLocation(program, 'u_worldXOffset'), offset);
            gl.uniform1f(gl.getUniformLocation(program, 'u_opacity'), this.opacity);

            const zoom = this.map!.getZoom();
            const radius = (typeof this._size === 'function' ? this._size(zoom) : this._size) / 2;
            const extrude = pixelsToTileUnits(radius, zoom);
            gl.uniform1f(gl.getUniformLocation(program, 'u_radius'), radius);
            gl.uniform2f(gl.getUniformLocation(program, 'u_extrude_scale'), extrude, extrude);
            gl.uniform2f(gl.getUniformLocation(program, 'u_image_size'), this.imageWidth, this.imageHeight);
            gl.uniform1f(gl.getUniformLocation(program, 'u_images_count'), this.imgsCount);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this.texture);
            gl.uniform1i(gl.getUniformLocation(program, 'u_texture'), 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.centerBuffer);
            gl.enableVertexAttribArray(this.aCenter);
            gl.vertexAttribPointer(this.aCenter, 2, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aCenter, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordsBuffer);
            gl.enableVertexAttribArray(this.aTexCoords);
            gl.vertexAttribPointer(this.aTexCoords, 2, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aTexCoords, 0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.texOffsetsBuffer);
            gl.enableVertexAttribArray(this.aTexOffset);
            gl.vertexAttribPointer(this.aTexOffset, 1, gl.UNSIGNED_BYTE, false, 0, 0);
            gl.vertexAttribDivisor(this.aTexOffset, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.visibilityBuffer);
            gl.enableVertexAttribArray(this.aVisibility);
            gl.vertexAttribPointer(this.aVisibility, 1, gl.UNSIGNED_BYTE, false, 0, 0);
            gl.vertexAttribDivisor(this.aVisibility, 1);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
            gl.enableVertexAttribArray(this.aPos);
            gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);
            gl.vertexAttribDivisor(this.aPos, 0);

            gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.instanceCount);
        });

        gl.vertexAttribDivisor(this.aCenter, 0);
        gl.vertexAttribDivisor(this.aTexCoords, 0);
        gl.vertexAttribDivisor(this.aTexOffset, 0);
        gl.vertexAttribDivisor(this.aVisibility, 0);
        gl.vertexAttribDivisor(this.aPos, 0);
    }

    private getSkipFactor(zoom: number): number {
        if (zoom <= 1) {
            return 8;
        }
        if (zoom <= 3) {
            return 4;
        }
        if (zoom <= 4) {
            return 2;
        }
        return 1;
    }

    private updateVisibilityBuffer(zoom: number) {
        if (!this.gl || !this.visibilityData) {
            return;
        }

        if (this.gridWidth == null || this.gridHeight == null) {
            // No grid data. Always show all items
            this.visibilityData.fill(1);
        } else {
            const skipFactor = this.getSkipFactor(zoom);
            this.visibilityData.fill(0);

            for (let row = 0; row < this.gridHeight; row += skipFactor) {
                for (let col = 0; col < this.gridWidth; col += skipFactor) {
                    const index = row * this.gridWidth + col;
                    if (index < this.instanceCount) {
                        this.visibilityData[index] = 1;
                    }
                }
            }
        }

        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.visibilityBuffer);
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.visibilityData);
    }

    private getWorldOffsets() {
        const bounds = this.map!.getBounds();
        const west = bounds.getWest();
        const east = bounds.getEast();

        const x1 = 0 - Math.ceil((Math.abs(west) - 180) / 360);
        const x2 = Math.ceil((Math.abs(east) - 180) / 360);

        const worldOffsets = [];
        for (let i = x1; i <= x2; i++) {
            worldOffsets.push(i);
        }
        return worldOffsets;
    }
}

export function pixelsToTileUnits(pixelValue: number, z: number): number {
    return pixelValue * (1.0 / (512 * Math.pow(2, z)));
}
